import { useAuth } from '@/hooks/useAuth';
import { useWalletAddressUserData } from '@/hooks/useWalletAddressUserData';
import { makeRequest } from '@/network/api';
import { AspectRatio, Box, Image as ChakraImage, Heading, useBreakpointValue, VStack } from '@chakra-ui/react';
import { useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useLocalStorage } from 'usehooks-ts';
import { OnboardingStep } from './constants';
import { ConvertMiles } from './ConvertMiles';
import { OnboardingCard } from './OnboardingCard';
import { OnboardingHeader } from './OnboardingHeader';
import plumeTokenSquareSvg from '/assets/onboarding/plume-token-square.svg';

import { Footer } from '@/components/footer';
import { LocalStorageKey, MAX_PAGE_WIDTH } from '@/constants/shared';
import { useSeasonOneStats } from '@/hooks/useSeasonOneStats';
import { AmplitudeTrackingEvent, trackAmplitudeEvent } from '@/lib/amplitude';
import { LottieReact } from '@lottielab/lottie-player';

export const Onboarding = () => {
  const { address, isAuthenticated, isLoading, hasCompletedOnboarding } = useAuth();
  const { miles } = useSeasonOneStats();
  const { stats } = useWalletAddressUserData(address);
  const navigate = useNavigate();
  const { currentStep = OnboardingStep.Welcome } = useParams<{ currentStep: OnboardingStep }>();
  const [furthestStepSeen, setFurthestStepSeen] = useLocalStorage(
    LocalStorageKey.FurthestStepSeen,
    OnboardingStep.Welcome,
  );
  const { referredByUser } = stats ?? {};
  const onboardingSteps = useMemo(
    () => Object.values(OnboardingStep).filter((stepName) => (miles ? true : stepName !== OnboardingStep.ConvertMiles)),
    [miles],
  );

  const currentStepNumber = onboardingSteps.indexOf(currentStep);
  const furthestStepIndex = onboardingSteps.indexOf(furthestStepSeen);

  useEffect(() => {
    const imagesToPreload = [
      '/assets/onboarding/earn-pp.avif',
      '/assets/onboarding/earn-plume.avif',
      // Add all other image paths here
    ];

    imagesToPreload.forEach((src) => {
      const img = new Image();
      img.src = src;
    });
  }, []);

  useEffect(() => {
    if (isLoading) return;
    else if (hasCompletedOnboarding) navigate('/');
    else if (!isAuthenticated && !isLoading) navigate('/');
    else if (currentStepNumber > furthestStepIndex) setFurthestStepSeen(currentStep);
    else if (!Object.values(OnboardingStep).includes(currentStep)) navigate('/onboarding');
  }, [
    currentStep,
    currentStepNumber,
    furthestStepIndex,
    hasCompletedOnboarding,
    isAuthenticated,
    isLoading,
    navigate,
    setFurthestStepSeen,
  ]);

  //   State for onboarding step management
  const goToNextStep = () => {
    const nextStep = onboardingSteps[currentStepNumber + 1];
    if (currentStepNumber + 1 > furthestStepIndex) setFurthestStepSeen(nextStep);
    if (currentStepNumber + 1 >= onboardingSteps.length) {
      completeOnboarding();
      return;
    }
    navigate(`/onboarding/${nextStep}`);
  };
  const goToPrevStep = () => {
    const prevStep = onboardingSteps[currentStepNumber - 1];
    navigate(`/onboarding/${prevStep}`);
  };

  const completeOnboarding = async () => {
    const res = await makeRequest('/user/onboarding/completion', {
      method: 'POST',
      body: JSON.stringify({
        hasCompletedOnboarding: true,
      }),
    });
    if (res.ok) {
      trackAmplitudeEvent(AmplitudeTrackingEvent.PortalCompletedOnboarding, {});
      navigate('/');
    } else {
      // TODO: error handle with a toast or something
      console.error('Error setting onboarding status');
    }
  };
  const shouldShowLottie = useBreakpointValue({
    base: false,
    sm: false,
    md: true,
  });

  return (
    <Box bgColor="bg.accent" minH="100vh" w="full" position="relative">
      <LottieReact
        src="https://cdn.lottielab.com/l/DB6wc5p7Ly5kb6.json"
        style={{
          position: 'absolute',
          height: '800px',
          top: 0,
          left: 0,
          width: '100%',
          zIndex: 0,
          pointerEvents: 'none',
        }}
      />
      <VStack
        maxW={MAX_PAGE_WIDTH}
        alignSelf="center"
        justifyContent="flex-start"
        alignItems="center"
        w="full"
        m="auto"
        py={8}
        gap={10}
      >
        <Box w="full" px={[0, 0, 3]}>
          <OnboardingHeader
            currentStepNumber={currentStepNumber}
            goToPrevStep={goToPrevStep}
            goToNextStep={goToNextStep}
            referredByUser={referredByUser}
            onboardingSteps={onboardingSteps}
          />
        </Box>
        <VStack
          px={[3, 3, 0]}
          w="full"
          alignSelf="center"
          justifyContent="flex-start"
          alignItems="center"
          m="auto"
          gap={10}
        >
          {currentStep === OnboardingStep.Welcome && (
            <OnboardingCard
              iconSrc={plumeTokenSquareSvg}
              title="Welcome to the Plume Portal"
              description="Dive into the Plume ecosystem and discover the vibrant world of RWAfi"
              renderImage={() => (
                <Box mx={-12}>
                  {shouldShowLottie ? (
                    <LottieReact src="https://cdn.lottielab.com/l/71YEeQjxYaof5H.json" />
                  ) : (
                    <ChakraImage src="/assets/onboarding/welcome.avif" />
                  )}
                </Box>
              )}
              ctaFunction={goToNextStep}
            />
          )}

          {currentStep === OnboardingStep.ConvertMiles && <ConvertMiles goToNextStep={goToNextStep} />}
          {currentStep === OnboardingStep.EarnRealYield && (
            <OnboardingCard
              topLabel="Earn Real Yield"
              title="Real World Assets"
              description={
                <Heading color="fg.muted" size="xl" textAlign="center" fontWeight="500">
                  Swap into RWA tokens and enjoy safe,{' '}
                  <Box as="span" color="fg">
                    stable yields that don’t go away
                  </Box>
                  . Backed by real-world assets, fully liquid, rigorously audited, and protected.
                </Heading>
              }
              renderImage={() => (
                <AspectRatio ratio={544 / 255} rounded="24px" overflow="hidden">
                  {shouldShowLottie ? (
                    <LottieReact src="https://cdn.lottielab.com/l/8sFgAb4XqjP64R.json" />
                  ) : (
                    <ChakraImage src="/assets/onboarding/earn-real-yield.avif" />
                  )}
                </AspectRatio>
              )}
              ctaFunction={goToNextStep}
            />
          )}
          {currentStep === OnboardingStep.EarnPP && (
            <OnboardingCard
              topLabel="Earn PP"
              title="Plume Points"
              description={
                <Heading color="fg.muted" size="xl" textAlign="center" fontWeight="500">
                  Engage with the Plume ecosystem by depositing TVL, with trading activity, completing quests, staking
                  your assets, and referring your friends.
                </Heading>
              }
              renderImage={() => (
                <AspectRatio ratio={544 / 255} rounded="24px" overflow="hidden">
                  {shouldShowLottie ? (
                    <LottieReact src="https://cdn.lottielab.com/l/DU3v7rjpHEHYCp.json" loop={false} />
                  ) : (
                    <ChakraImage src="/assets/onboarding/earn-pp.avif" />
                  )}
                </AspectRatio>
              )}
              ctaFunction={goToNextStep}
              slotBottom={
                <Heading fontSize={['lg', 'lg', 'xl']} color="fg.muted" textAlign="center" fontWeight="500">
                  The more Plume Points you earn, the better your rewards in Season 2!
                </Heading>
              }
            />
          )}
          {currentStep === OnboardingStep.EarnPlume && (
            <OnboardingCard
              topLabel="Earn PLUME"
              title="PLUME Incentives"
              description={
                <Heading color="fg.muted" size="xl" textAlign="center" fontWeight="500">
                  Also earn PLUME by leveraging the Yield Optimizer, joining Royco campaigns, and staking your PLUME.
                </Heading>
              }
              renderImage={() => (
                <AspectRatio ratio={544 / 255} rounded="24px" overflow="hidden">
                  {shouldShowLottie ? (
                    <LottieReact src="https://cdn.lottielab.com/l/2nuqpUWt92YpAt.json" loop={false} />
                  ) : (
                    <ChakraImage src="/assets/onboarding/earn-plume.avif" />
                  )}
                </AspectRatio>
              )}
              ctaFunction={goToNextStep}
            />
          )}
        </VStack>
      </VStack>
      <Footer />
    </Box>
  );
};
